import {
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Input,
  ListItemText,
  MenuItem,
  NativeSelect,
  Paper,
  Select,
  Tooltip,
} from '@material-ui/core';
import CancelIcon from '@material-ui/icons/Cancel';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import MaterialTable from 'material-table';
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useHistory, useLocation } from 'react-router-dom';

import Loading from '../../components/Loading';
import Header from '../../layout/Header';
import Subheader from '../../layout/Subheader';
import api from '../../services/api';
import { findPermission } from '../../services/auth';
import { materialTableLocalizationPtBR } from '../../services/materialTableLocalization';
import {
  DEFAULT_PERIODO_MAX_DIAS,
  getDefaultPeriodoRange,
  validatePeriodoRange,
} from '../../utils/dateRangeValidation';

import useStyles from './style';

export const MODULO_ID_PAGAMENTOS_REMESSAS = 18198;
const MODULO_ID_CONSULTAR_REMESSAS = 16320;

const STATUS_OPTIONS = [
  { value: 0, name: 'Pendente',    label: 'Pendente',     color: '#FB8832' },
  { value: 1, name: 'Processando', label: 'Processando', color: '#1E88E5' },
  { value: 2, name: 'Enviado',     label: 'Enviado',      color: '#26A69A' },
  { value: 3, name: 'Erro',        label: 'Erro',         color: '#C62828' },
  { value: 4, name: 'Pago',        label: 'Pago',         color: '#2E7D32' },
  { value: 5, name: 'PagoManual',  label: 'Pago Manual',  color: '#7CB342' },
  { value: 6, name: 'Cancelado',   label: 'Cancelado',    color: '#9E9E9E' },
  { value: 7, name: 'Agendado',    label: 'Agendado Banco', color: '#7B1FA2' },
];

const statusToInt = (s) => {
  if (typeof s === 'number') return s;
  const found = STATUS_OPTIONS.find((x) => x.name === s);
  return found ? found.value : -1;
};

const SELECIONAVEIS = [0, 3];

const PERIODO_MAX_DIAS = DEFAULT_PERIODO_MAX_DIAS;

const formatBrl = (v) =>
  Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const formatDate = (d) => {
  if (!d) return '';
  try {
    return new Date(d).toLocaleDateString('pt-BR');
  } catch (_) {
    return '';
  }
};

const statusLabel = (s) => STATUS_OPTIONS.find((x) => x.value === statusToInt(s))?.label || s;
const statusColor = (s) => STATUS_OPTIONS.find((x) => x.value === statusToInt(s))?.color || '#777';

export function PagamentosRemessas() {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();

  const [isLoading, setLoading] = useState(false);
  const [destinos, setDestinos] = useState([]);
  const [ancoras, setAncoras] = useState([]);
  const [bancosRemessasDestino, setBancosRemessasDestino] = useState([]);
  const [rows, setRows] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);

  const [destinoPagamentoId, setDestinoPagamentoId] = useState('');
  const [ancoraId, setAncoraId] = useState('');
  const [fornecedorCnpj, setFornecedorCnpj] = useState('');
  const [fornecedorRazaoSocial, setFornecedorRazaoSocial] = useState('');
  const [dataInicial, setDataInicial] = useState(() => getDefaultPeriodoRange(1).inicial);
  const [dataFinal, setDataFinal] = useState(() => getDefaultPeriodoRange(1).final);
  const [codigoRemessa, setCodigoRemessa] = useState('');
  const [statusFiltro, setStatusFiltro] = useState([]);

  const [autoPesquisarDeepLink, setAutoPesquisarDeepLink] = useState(false);

  const [erroModal, setErroModal] = useState({ open: false, observacao: '' });
  const [baixarModal, setBaixarModal] = useState({ open: false, confirmText: '' });
  const [desvincularModal, setDesvincularModal] = useState({ open: false, confirmText: '' });
  const [criarModal, setCriarModal] = useState({ open: false, confirmText: '', bancoRemessaId: '' });

  useEffect(() => {
    if (!findPermission(MODULO_ID_PAGAMENTOS_REMESSAS, 'Consultar')) {
      history.replace('/app/home');
    }
  }, [history]);

  const fetchFilterOptions = async () => {
    try {
      const [dRes, aRes] = await Promise.all([
        api.get('DestinosPagamentos/Consultar?status=Ativo'),
        api.get('Ancoras/Consultar?status=Ativo'),
      ]);
      const destinosCarregados = dRes?.data?.resposta || [];
      setDestinos(destinosCarregados);
      setAncoras(aRes?.data?.resposta || []);
      return destinosCarregados;
    } catch (e) {
      toast.error('Erro ao carregar filtros.');
      return [];
    }
  };

  const fetchBancosDoDestino = async (destinoId) => {
    if (!destinoId) {
      setBancosRemessasDestino([]);
      return;
    }
    try {
      const [assocRes, bancosRes] = await Promise.all([
        api.get(`DestinosPagamentos/ConsultarBancosRemessas?destinoPagamentoId=${destinoId}`),
        api.get('BancosRemessas/Consultar?status=Ativo'),
      ]);
      const ids = (assocRes?.data?.resposta || []).map((x) => x.bancoRemessaId);
      const bancos = bancosRes?.data?.resposta || [];
      setBancosRemessasDestino(bancos.filter((b) => ids.includes(b.bancoRemessaId)));
    } catch (e) {
      setBancosRemessasDestino([]);
    }
  };

  const podePesquisar = !!destinoPagamentoId;

  const refreshPagamentos = () => fetchPagamentos({ silencioso: true });

  const fetchPagamentos = async ({ silencioso = false } = {}) => {
    if (!podePesquisar) {
      if (!silencioso) toast.warn('Selecione um Destino de Pagamento para pesquisar.');
      return;
    }
    if (!codigoRemessa) {
      const periodo = validatePeriodoRange(dataInicial, dataFinal, PERIODO_MAX_DIAS);
      if (!periodo.valid) {
        if (!silencioso) toast.warn(periodo.mensagem);
        return;
      }
    }
    setLoading(true);
    setSelectedIds([]);
    try {
      const params = new URLSearchParams();
      params.append('destinoPagamentoId', destinoPagamentoId);
      if (ancoraId) params.append('ancoraId', ancoraId);
      if (fornecedorCnpj) params.append('fornecedorCnpj', fornecedorCnpj);
      if (fornecedorRazaoSocial) params.append('fornecedorRazaoSocial', fornecedorRazaoSocial);
      if (dataInicial) params.append('dataInicial', dataInicial);
      if (dataFinal) params.append('dataFinal', dataFinal);
      if (codigoRemessa) params.append('codigoRemessa', codigoRemessa);
      statusFiltro.forEach((s) => params.append('status', s));

      const res = await api.get(`AntecipacoesPagamentosRemessas/Consultar?${params.toString()}`);
      setRows(res?.data?.resposta || []);
    } catch (e) {
      toast.error('Erro ao consultar pagamentos.');
      setRows([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    (async () => {
      const destinosCarregados = await fetchFilterOptions();
      const params = new URLSearchParams(location.search);
      const remessaParam = params.get('remessa');
      const destinoParam = params.get('destino');
      const dataIniParam = params.get('dataInicial');
      const dataFimParam = params.get('dataFinal');
      if (remessaParam) setCodigoRemessa(remessaParam);
      if (destinoParam) {
        setDestinoPagamentoId(destinoParam);
      } else {
        const securitizadora = destinosCarregados.find(
          (d) => (d.destinoPagamento || d.nomeFantasia || '').toUpperCase() === 'SECURITIZADORA',
        );
        if (securitizadora) setDestinoPagamentoId(securitizadora.destinoPagamentoId);
      }
      if (dataIniParam) setDataInicial(dataIniParam);
      if (dataFimParam) setDataFinal(dataFimParam);
      if (remessaParam && !dataIniParam && !dataFimParam) {
        setDataInicial('');
        setDataFinal('');
      }
      if (remessaParam && destinoParam) {
        setAutoPesquisarDeepLink(true);
      } else if (remessaParam || destinoParam) {
        toast.info('Filtros vindos da tela anterior pré-preenchidos. Clique em Pesquisar.');
      }
    })();
  }, []);

  const abrirEmOperacao = async (row) => {
    if (!row?.antecipacaoId) {
      toast.error('Antecipação não identificada para esta linha.');
      return;
    }
    try {
      const resp = await api.get(`Antecipacoes/ConsultarId?id=${row.antecipacaoId}`);
      const a = Array.isArray(resp?.data?.resposta) ? resp.data.resposta[0] : resp?.data?.resposta;
      if (!a) {
        toast.error('Não foi possível carregar a antecipação selecionada.');
        return;
      }
      const payload = {
        ancoraId: a.ancoraId ?? row.ancoraId,
        antecipacaoId: a.antecipacaoId ?? row.antecipacaoId,
        fornecedorId: a.fornecedorId ?? row.fornecedorId,
        destinoPagamentoId: a.destinoPagamentoId ?? row.destinoPagamentoId,
      };
      if (!payload.ancoraId || !payload.destinoPagamentoId) {
        toast.error('Dados insuficientes para abrir a operação (âncora/destino).');
        return;
      }
      window.localStorage.setItem('operationFromReport', JSON.stringify(payload));
      sessionStorage.setItem('DestinoPagamentoIdAtual', payload.destinoPagamentoId);
      window.location = '/app/operacao-antecipacao';
    } catch (e) {
      console.error('Erro ao pré-carregar antecipação para navegação:', e);
      toast.error('Falha ao abrir a antecipação. Tente novamente.');
    }
  };

  const irParaConsultarRemessas = () => {
    const params = new URLSearchParams();
    if (destinoPagamentoId) params.append('destino', destinoPagamentoId);
    if (dataInicial) params.append('dataInicial', dataInicial);
    if (dataFinal) params.append('dataFinal', dataFinal);
    if (codigoRemessa) params.append('codigo', codigoRemessa);
    history.push(`/app/consultar-remessas${params.toString() ? `?${params.toString()}` : ''}`);
  };

  useEffect(() => {
    fetchBancosDoDestino(destinoPagamentoId);
  }, [destinoPagamentoId]);

  useEffect(() => {
    if (autoPesquisarDeepLink && destinoPagamentoId && codigoRemessa) {
      setAutoPesquisarDeepLink(false);
      fetchPagamentos();
    }
  }, [autoPesquisarDeepLink, destinoPagamentoId, codigoRemessa]);

  const valorSelecionado = useMemo(
    () =>
      rows
        .filter((r) => selectedIds.includes(r.antecipacaoPagamentoRemessaId))
        .reduce((acc, r) => acc + Number(r.valorTotal || 0), 0),
    [selectedIds, rows],
  );

  const valorTotalGrid = useMemo(
    () => rows.reduce((acc, r) => acc + Number(r.valorTotal || 0), 0),
    [rows],
  );

  const toggleAll = () => {
    const elegiveis = rows.filter((r) => SELECIONAVEIS.includes(statusToInt(r.status))).map((r) => r.antecipacaoPagamentoRemessaId);
    if (elegiveis.every((id) => selectedIds.includes(id))) {
      setSelectedIds([]);
    } else {
      setSelectedIds(elegiveis);
    }
  };

  const toggleOne = (row) => {
    if (!SELECIONAVEIS.includes(statusToInt(row.status))) return;
    const id = row.antecipacaoPagamentoRemessaId;
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const cancelarPagamento = async (row) => {
    if (!window.confirm('Confirma cancelar este pagamento?')) return;
    try {
      await api.put('AntecipacoesPagamentosRemessas/Cancelar', {
        antecipacaoPagamentoRemessaId: row.antecipacaoPagamentoRemessaId,
      });
      toast.success('Pagamento cancelado.');
      refreshPagamentos();
    } catch (e) {
      toast.error(e?.response?.data?.notificacoes?.[0]?.mensagem || 'Erro ao cancelar.');
    }
  };

  const confirmarBaixar = async () => {
    try {
      await api.put('AntecipacoesPagamentosRemessas/BaixarPagamentos', {
        antecipacoesPagamentosRemessasIds: selectedIds,
      });
      toast.success(`${selectedIds.length} pagamento(s) baixado(s).`);
      setBaixarModal({ open: false, confirmText: '' });
      refreshPagamentos();
    } catch (e) {
      toast.error(e?.response?.data?.notificacoes?.[0]?.mensagem || 'Erro ao baixar.');
    }
  };

  const confirmarDesvincular = async () => {
    try {
      await api.put('AntecipacoesPagamentosRemessas/DesvincularErro', {
        antecipacoesPagamentosRemessasIds: selectedIds,
      });
      toast.success(`${selectedIds.length} pagamento(s) desvinculado(s) da remessa.`);
      setDesvincularModal({ open: false, confirmText: '' });
      refreshPagamentos();
    } catch (e) {
      toast.error(e?.response?.data?.notificacoes?.[0]?.mensagem || 'Erro ao desvincular.');
    }
  };

  const confirmarCriarRemessa = async () => {
    try {
      const res = await api.post('AntecipacoesPagamentosRemessas/CriarRemessa', {
        antecipacoesPagamentosRemessasIds: selectedIds,
        bancoRemessaId: Number(criarModal.bancoRemessaId),
      });
      toast.success(`Remessa ${res?.data?.resposta?.codigoInterno || ''} criada.`);
      setCriarModal({ open: false, confirmText: '', bancoRemessaId: '' });
      refreshPagamentos();
    } catch (e) {
      toast.error(e?.response?.data?.notificacoes?.[0]?.mensagem || 'Erro ao criar remessa.');
    }
  };

  const linhasElegiveis = rows.filter((r) => SELECIONAVEIS.includes(statusToInt(r.status)));
  const allElegiveisMarcados =
    linhasElegiveis.length > 0 &&
    linhasElegiveis.every((r) => selectedIds.includes(r.antecipacaoPagamentoRemessaId));

  const columns = useMemo(
    () => [
      { title: 'Data Pgto Previsto', field: 'dataPagamentoPrevista', render: (r) => formatDate(r.dataPagamentoPrevista) || '-' },
      { title: 'Data Pago', field: 'dataPagamento', render: (r) => formatDate(r.dataPagamento) || '-' },
      { title: 'Dest. Pagto', field: 'destinoPagamentoDescricao' },
      {
        title: 'ID Antec.',
        field: 'antecipacaoId',
        render: (r) =>
          r.antecipacaoId ? (
            <span className={classes.linkCell} onClick={() => abrirEmOperacao(r)}>
              {r.antecipacaoId}
            </span>
          ) : (
            '-'
          ),
      },
      { title: 'Banco Remessa', field: 'bancoRemessaDescricao' },
      {
        title: 'Remessa',
        field: 'codigoRemessa',
        render: (r) =>
          r.codigoRemessa ? (
            <span
              className={classes.linkCell}
              onClick={() => history.push(`/app/consultar-remessas?codigo=${r.codigoRemessa}`)}
            >
              {r.codigoRemessa}
            </span>
          ) : (
            '-'
          ),
      },
      { title: 'Rede', field: 'ancoraNomeFantasia' },
      { title: 'CNPJ', field: 'fornecedorCnpjCpf' },
      {
        title: 'Razão Social',
        field: 'fornecedorRazaoSocial',
        render: (r) => (
          <span
            className={classes.linkCell}
            onClick={() => abrirEmOperacao(r)}
          >
            {r.fornecedorRazaoSocial}
          </span>
        ),
      },
      {
        title: 'Valor',
        field: 'valorTotal',
        render: (r) => formatBrl(r.valorTotal),
        cellStyle: { textAlign: 'right' },
        defaultSort: 'asc',
      },
      {
        title: 'Status',
        field: 'status',
        render: (r) => (
          <span className={classes.statusChip} style={{ background: statusColor(r.status) }}>
            {statusLabel(r.status)}
          </span>
        ),
      },
      {
        title: 'Erro',
        field: 'erro',
        sorting: false,
        render: (r) =>
          statusToInt(r.status) === 3 ? (
            <span
              className={classes.errorLink}
              onClick={() => setErroModal({ open: true, observacao: r.observacao || 'Sem detalhe.' })}
            >
              <ErrorOutlineIcon fontSize="small" /> Ver
            </span>
          ) : null,
      },
      {
        title: (
          <Checkbox
            checked={allElegiveisMarcados}
            onChange={toggleAll}
            disabled={linhasElegiveis.length === 0}
            style={{ padding: 0 }}
          />
        ),
        field: 'check',
        sorting: false,
        cellStyle: { width: 40, padding: '4px 8px' },
        render: (row) =>
          SELECIONAVEIS.includes(statusToInt(row.status)) ? (
            <Checkbox
              checked={selectedIds.includes(row.antecipacaoPagamentoRemessaId)}
              onChange={() => toggleOne(row)}
              style={{ padding: 0 }}
            />
          ) : null,
      },
      {
        title: 'Ações',
        field: 'acoes',
        sorting: false,
        render: (r) =>
          SELECIONAVEIS.includes(statusToInt(r.status)) && findPermission(MODULO_ID_PAGAMENTOS_REMESSAS, 'Remover') ? (
            <Tooltip title="Cancelar pagamento">
              <IconButton size="small" onClick={() => cancelarPagamento(r)}>
                <CancelIcon style={{ color: '#C62828' }} />
              </IconButton>
            </Tooltip>
          ) : null,
      },
    ],
    [rows, selectedIds, allElegiveisMarcados, linhasElegiveis.length],
  );

  return (
    <div className={classes.root}>
      <Header />
      <Subheader title="Pagamentos Remessas" back="/app/home" />
      <div className={classes.content}>
        <div className={classes.rootForm}>
          <header>
          <Grid container className={classes.searchContainer} spacing={2}>
            <Grid item xs={3} className={classes.item}>
              <label className={classes.label}>Destino de Pagamento *</label>
              <Paper className={classes.medium} elevation={0}>
                <NativeSelect
                  className={classes.input}
                  value={destinoPagamentoId}
                  onChange={(e) => setDestinoPagamentoId(e.target.value)}
                >
                  <option value="">Selecione</option>
                  {destinos.map((d) => (
                    <option key={d.destinoPagamentoId} value={d.destinoPagamentoId}>
                      {d.destinoPagamento || d.nomeFantasia}
                    </option>
                  ))}
                </NativeSelect>
              </Paper>
            </Grid>
            <Grid item xs={2} className={classes.item}>
              <label className={classes.label}>Rede</label>
              <Paper className={classes.medium} elevation={0}>
                <NativeSelect
                  className={classes.input}
                  value={ancoraId}
                  onChange={(e) => setAncoraId(e.target.value)}
                >
                  <option value="">Todas</option>
                  {ancoras.map((a) => (
                    <option key={a.ancoraId} value={a.ancoraId}>
                      {a.nomeFantasia}
                    </option>
                  ))}
                </NativeSelect>
              </Paper>
            </Grid>
            <Grid item xs={2} className={classes.item}>
              <label className={classes.label}>Fornecedor</label>
              <Paper className={classes.medium} elevation={0}>
                <Input
                  className={classes.input}
                  value={fornecedorRazaoSocial}
                  placeholder="Razão Social"
                  onChange={(e) => setFornecedorRazaoSocial(e.target.value)}
                />
              </Paper>
            </Grid>
            <Grid item xs={2} className={classes.item}>
              <label className={classes.label}>Fornecedor CNPJ</label>
              <Paper className={classes.medium} elevation={0}>
                <Input
                  className={classes.input}
                  value={fornecedorCnpj}
                  onChange={(e) => setFornecedorCnpj(e.target.value.replace(/\D/g, ''))}
                  inputProps={{ maxLength: 14 }}
                />
              </Paper>
            </Grid>
            <Grid item xs={2} className={classes.item}>
              <label className={classes.label}>Remessa</label>
              <Paper className={classes.medium} elevation={0}>
                <Input
                  className={classes.input}
                  value={codigoRemessa}
                  onChange={(e) => setCodigoRemessa(e.target.value)}
                />
              </Paper>
            </Grid>
            <Grid item xs={2} className={classes.item}>
              <label className={classes.label}>Data Prev. Pgto Inicial</label>
              <Paper className={classes.medium} elevation={0}>
                <Input
                  type="date"
                  className={classes.input}
                  value={dataInicial}
                  onChange={(e) => setDataInicial(e.target.value)}
                />
              </Paper>
            </Grid>
            <Grid item xs={2} className={classes.item}>
              <label className={classes.label}>Data Prev. Pgto Final</label>
              <Paper className={classes.medium} elevation={0}>
                <Input
                  type="date"
                  className={classes.input}
                  value={dataFinal}
                  onChange={(e) => setDataFinal(e.target.value)}
                />
              </Paper>
            </Grid>
            <Grid item xs={2} className={classes.item}>
              <label className={classes.label}>Status</label>
              <Paper className={classes.medium} elevation={0}>
                <Select
                  multiple
                  disableUnderline
                  displayEmpty
                  className={classes.input}
                  value={statusFiltro}
                  onChange={(e) => setStatusFiltro(e.target.value)}
                  renderValue={(selected) => {
                    if (!selected || selected.length === 0) return 'Todos';
                    if (selected.length === STATUS_OPTIONS.length) return 'Todos';
                    if (selected.length === 1) return statusLabel(selected[0]);
                    return `${selected.length} selecionados`;
                  }}
                  MenuProps={{
                    variant: 'menu',
                    getContentAnchorEl: null,
                    anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                    transformOrigin: { vertical: 'top', horizontal: 'left' },
                    autoFocus: false,
                    PaperProps: {
                      style: {
                        maxHeight: 220,
                        overflowY: 'scroll',
                        width: 240,
                      },
                    },
                    MenuListProps: { style: { padding: 0, width: 240 } },
                  }}
                >
                  {STATUS_OPTIONS.map((s) => (
                    <MenuItem key={s.value} value={s.value} dense style={{ paddingLeft: 4 }}>
                      <Checkbox
                        checked={statusFiltro.indexOf(s.value) > -1}
                        size="small"
                        style={{ color: s.color, padding: 4 }}
                      />
                      <span
                        style={{
                          display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
                          background: s.color, marginRight: 8, flexShrink: 0,
                        }}
                      />
                      <ListItemText
                        primary={s.label}
                        primaryTypographyProps={{ style: { fontSize: 13 } }}
                      />
                    </MenuItem>
                  ))}
                </Select>
              </Paper>
            </Grid>
          </Grid>
          </header>
          <div className={classes.actions}>
            <button
              className={classes.buttonFilter}
              onClick={fetchPagamentos}
              disabled={!podePesquisar}
            >
              Pesquisar
            </button>
            {!!findPermission(MODULO_ID_CONSULTAR_REMESSAS, 'Consultar') && (
              <button
                className={classes.buttonAction}
                onClick={irParaConsultarRemessas}
                title="Abrir Consultar Remessas mantendo o contexto"
              >
                Ver em Consultar Remessas
              </button>
            )}
          </div>
          {isLoading ? (
            <Loading />
          ) : (
            <>
              <MaterialTable
                options={{
                  showTitle: false,
                  search: false,
                  toolbar: false,
                  paging: false,
                  filtering: false,
                  headerStyle: { backgroundColor: '#F5F5F5', fontSize: '13px' },
                  rowStyle: { fontSize: '13px' },
                }}
                localization={materialTableLocalizationPtBR}
                columns={columns}
                data={rows}
              />
              <div className={classes.footer}>
                <div className={classes.totals}>
                  <span>Valor Total Selecionado: {formatBrl(valorSelecionado)}</span>
                  <span style={{ fontWeight: 'normal', fontSize: '12px', color: '#555' }}>
                    Total geral exibido: {formatBrl(valorTotalGrid)} ({rows.length} registros)
                  </span>
                </div>
                <div>
                  <button
                    className={classes.buttonAction}
                    disabled={selectedIds.length === 0 || !findPermission(MODULO_ID_PAGAMENTOS_REMESSAS, 'Alterar')}
                    onClick={() => setBaixarModal({ open: true, confirmText: '' })}
                  >
                    Baixar Pagamentos
                  </button>
                  <button
                    className={classes.buttonAction}
                    disabled={selectedIds.length === 0 || !findPermission(MODULO_ID_PAGAMENTOS_REMESSAS, 'Cadastrar')}
                    onClick={() => setCriarModal({ open: true, confirmText: '', bancoRemessaId: '' })}
                  >
                    Criar Remessa
                  </button>
                  <button
                    className={classes.buttonAction}
                    style={{ background: '#C62828' }}
                    disabled={selectedIds.length === 0 || !findPermission(MODULO_ID_PAGAMENTOS_REMESSAS, 'Alterar')}
                    onClick={() => setDesvincularModal({ open: true, confirmText: '' })}
                  >
                    Desvincular Pagamentos com Erro
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <Dialog open={erroModal.open} onClose={() => setErroModal({ open: false, observacao: '' })}>
        <DialogTitle>Detalhes do Erro</DialogTitle>
        <DialogContent style={{ minWidth: '420px' }}>{erroModal.observacao}</DialogContent>
        <DialogActions>
          <button className={classes.buttonAction} onClick={() => setErroModal({ open: false, observacao: '' })}>
            Fechar
          </button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={baixarModal.open}
        onClose={() => setBaixarModal({ open: false, confirmText: '' })}
      >
        <DialogTitle>Confirma baixar os Pagamentos?</DialogTitle>
        <DialogContent>
          <div className={classes.modalBody}>
            <div className={classes.modalRow}>
              <span className={classes.modalLabel}>Quantidade:</span>
              <span>{selectedIds.length}</span>
            </div>
            <div className={classes.modalRow}>
              <span className={classes.modalLabel}>Valor total:</span>
              <span>{formatBrl(valorSelecionado)}</span>
            </div>
            <div style={{ marginTop: '12px' }}>
              <label className={classes.modalLabel}>Para confirmar, digite "baixar":</label>
              <input
                className={classes.modalConfirmInput}
                autoComplete="off"
                value={baixarModal.confirmText}
                onChange={(e) =>
                  setBaixarModal((s) => ({ ...s, confirmText: e.target.value }))
                }
              />
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <button
            className={classes.buttonAction}
            style={{ background: '#9E9E9E' }}
            onClick={() => setBaixarModal({ open: false, confirmText: '' })}
          >
            Cancelar
          </button>
          <button
            className={classes.buttonAction}
            disabled={baixarModal.confirmText.trim().toLowerCase() !== 'baixar'}
            onClick={confirmarBaixar}
          >
            Confirmar
          </button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={desvincularModal.open}
        onClose={() => setDesvincularModal({ open: false, confirmText: '' })}
      >
        <DialogTitle style={{ background: '#C62828', color: '#fff' }}>Confirma desvincular os Pagamentos?</DialogTitle>
        <DialogContent>
          <div className={classes.modalBody}>
            <p style={{ marginTop: 0 }}>
              Use isto só para pagamentos com <strong>Erro</strong> que já foram para o banco e vieram
              rejeitados no retorno (a remessa deles já foi enviada, não dá mais pra cancelar). Ao
              desvincular, o pagamento sai da remessa (fica livre pra corrigir e reenviar numa remessa
              nova), mas continua marcado como Erro até você agir sobre ele.
            </p>
            <div className={classes.modalRow}>
              <span className={classes.modalLabel}>Quantidade:</span>
              <span>{selectedIds.length}</span>
            </div>
            <div className={classes.modalRow}>
              <span className={classes.modalLabel}>Valor total:</span>
              <span>{formatBrl(valorSelecionado)}</span>
            </div>
            <div style={{ marginTop: '12px' }}>
              <label className={classes.modalLabel}>Para confirmar, digite "desvincular":</label>
              <input
                className={classes.modalConfirmInput}
                autoComplete="off"
                value={desvincularModal.confirmText}
                onChange={(e) =>
                  setDesvincularModal((s) => ({ ...s, confirmText: e.target.value }))
                }
              />
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <button
            className={classes.buttonAction}
            style={{ background: '#9E9E9E' }}
            onClick={() => setDesvincularModal({ open: false, confirmText: '' })}
          >
            Cancelar
          </button>
          <button
            className={classes.buttonAction}
            style={{ background: '#C62828' }}
            disabled={desvincularModal.confirmText.trim().toLowerCase() !== 'desvincular'}
            onClick={confirmarDesvincular}
          >
            Confirmar
          </button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={criarModal.open}
        onClose={() => setCriarModal({ open: false, confirmText: '', bancoRemessaId: '' })}
      >
        <DialogTitle>Confirma criar Remessa?</DialogTitle>
        <DialogContent>
          <div className={classes.modalBody}>
            <div className={classes.modalRow}>
              <span className={classes.modalLabel}>Quantidade:</span>
              <span>{selectedIds.length}</span>
            </div>
            <div className={classes.modalRow}>
              <span className={classes.modalLabel}>Valor total:</span>
              <span>{formatBrl(valorSelecionado)}</span>
            </div>
            <div style={{ marginTop: '12px' }}>
              <label className={classes.modalLabel}>Banco Remessa:</label>
              <NativeSelect
                className={classes.modalConfirmInput}
                value={criarModal.bancoRemessaId}
                onChange={(e) =>
                  setCriarModal((s) => ({ ...s, bancoRemessaId: e.target.value }))
                }
              >
                <option value="">Selecione</option>
                {bancosRemessasDestino.map((b) => (
                  <option key={b.bancoRemessaId} value={b.bancoRemessaId}>
                    {b.nome}
                  </option>
                ))}
              </NativeSelect>
            </div>
            <div style={{ marginTop: '12px' }}>
              <label className={classes.modalLabel}>Para confirmar, digite "criar":</label>
              <input
                className={classes.modalConfirmInput}
                autoComplete="off"
                value={criarModal.confirmText}
                onChange={(e) =>
                  setCriarModal((s) => ({ ...s, confirmText: e.target.value }))
                }
              />
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <button
            className={classes.buttonAction}
            style={{ background: '#9E9E9E' }}
            onClick={() => setCriarModal({ open: false, confirmText: '', bancoRemessaId: '' })}
          >
            Cancelar
          </button>
          <button
            className={classes.buttonAction}
            disabled={
              criarModal.confirmText.trim().toLowerCase() !== 'criar' ||
              !criarModal.bancoRemessaId
            }
            onClick={confirmarCriarRemessa}
          >
            Confirmar
          </button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default PagamentosRemessas;
